import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { Evento } from '../../api/ops/eventos';
import type { Politica } from '../../api/ops/pagos';
import type { Reserva } from '../../api/ops/reservas';
import type { Oferta } from '../../api/ops/reservasGestion';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { CampoImporte } from '../../componentes/CampoImporte';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { aCentimos, dinero, textoDeCentimos } from '../../logica/dinero';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import { nombreDe, todas, useNombres, type ContextoReservas } from '../reservas/contexto';
import { diaCorto, horaEn, hoyEn } from '../reservas/fechas';
import { mayuscula } from '../reservas/textos';
import { PRECIO } from './textos';

/**
 * 8.10 Reservados para eventos (apartado de Recursos, `?apartado=reservados`; G17, G18, G21, G16, G520): aquí no se crean
 * recursos, se ponen a la venta zonas o recursos que ya existen para un evento (`listEventOffers`, `createEventOffer`,
 * `updateEventOffer` con `If-Match`, `withdrawEventOffer`), con su precio, su política y el cupo de RRPP (con el bloque
 * `listas_rrpp`; cuenta personas, G520). «Por recolocar» (`listReservations` con `needs_relocation`, las de un evento):
 * cuando el evento cambia de fecha y no caben (G17), recolocar a mano (`assignReservationResources`; si el recurso es otro,
 * `409 confirmation_required` y «Recolocar y avisar», G16) o cancelar con devolución íntegra (`cancelReservation`). Sin el
 * bloque de Eventos y Entradas, lo dice. Gestionan Propietario y Administrador (`reservas.configuracion.gestionar`).
 */
/** `EventOffer.status` admite `withdrawn` (EventOfferStatus) aunque los campos de escritura no: se lee como texto. */
const ESTADO: Readonly<Record<string, readonly [string, EstadoChip]>> = {
    draft: ['Borrador', 'draft'],
    on_sale: ['A la venta', 'confirmed'],
    paused: ['Pausado', 'pending'],
    withdrawn: ['Retirado', 'neutral'],
};
const MODO_PRECIO = ['per_reservation', 'per_person', 'per_resource'] as const;
type ModoPrecio = (typeof MODO_PRECIO)[number];

interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    soloLectura: boolean;
    gestiona: boolean;
    contexto: ContextoReservas;
    nuevo: number;
    navegar: (ruta: string) => void;
    /** `?filtro=` de la ruta: `recolocar` abre «Por recolocar» (el enlace del aviso `reservas.reservados_por_recolocar`, G795). */
    filtro?: string | null;
}

type Vista = 'venta' | 'recolocar';

interface Datos {
    ofertas: Oferta[];
    recolocar: Reserva[];
}

export function ReservadosEventos({ ops, negocio, movil, soloLectura, gestiona, contexto, nuevo, navegar, filtro = null }: Props) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [vista, setVista] = useState<Vista>(filtro === 'recolocar' ? 'recolocar' : 'venta');
    const [editar, setEditar] = useState<Oferta | null>(null);
    // «Poner … a la venta» de la cabecera sube `nuevo`; el panel nuevo queda abierto hasta que se cierra con ese valor.
    const [nuevaCerrada, setNuevaCerrada] = useState(0);
    const nueva = gestiona && nuevo > nuevaCerrada;
    const recargar = useCallback(() => { setIntento((i) => i + 1); }, []);
    const contratado = contexto.bloques.length === 0 || contexto.bloques.includes('entradas_eventos');

    useEffect(() => {
        let vivo = true;
        Promise.all([
            todas((cursor) => ops.listEventOffers(negocio, { cursor, limit: 100 })),
            todas((cursor) => ops.listReservations(negocio, { needs_relocation: true, cursor, limit: 100 })),
        ])
            .then(([ofertas, recolocar]) => {
                if (vivo) {
                    setDatos({ ofertas: ofertas.filter((o) => (o.status as string) !== 'withdrawn'), recolocar: recolocar.filter((r) => r.event_id != null && ['confirmed', 'pending_payment'].includes(r.status)) });
                    setError(null);
                }
            })
            .catch((e: unknown) => { if (vivo) { setError(e); } });
        return () => { vivo = false; };
    }, [ops, negocio, intento]);

    const nombres = useNombres(ops, negocio, (datos?.recolocar ?? []).map((r) => r.customer_id ?? '').filter((x) => x !== ''), permisos.puede('clientes.fichas.ver'));

    if (!contratado) {
        return (
            <div className="rv-rc-aviso rv-rc-aviso--neutro" role="note">
                <span className="rv-rc-aviso__icono" aria-hidden="true"><Icono nombre="ticket" /></span>
                <span><strong>Necesitas Eventos y Entradas</strong><small>Para poner tus zonas o tus reservados a la venta para un evento, contrata el bloque de Eventos y Entradas.</small></span>
            </div>
        );
    }
    if (error !== null && datos === null) {
        const e = estadoDeError(error);
        return e.tipo === 'sin-permiso'
            ? <EstadoSinPermiso seccion="Reservados para eventos" rol={contexto.rol} negocio={contexto.nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />
            : <EstadoError seccion="Reservados para eventos" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="Reservados para eventos" />;
    }

    const destino = (o: Pick<Oferta, 'target'>) => {
        if (o.target.zone_id !== undefined) {
            return { icono: 'layers' as const, nombre: contexto.zonas.find((z) => z.id === o.target.zone_id)?.name ?? 'Zona', tipo: 'Zona' };
        }
        const r = contexto.recursos.find((x) => x.id === o.target.resource_id);
        const zona = contexto.zonas.find((z) => z.id === r?.current_terms.zone_id)?.name ?? '';
        return { icono: 'layout-grid' as const, nombre: `${mayuscula(contexto.palabra)} ${r?.code ?? ''}`, tipo: `${mayuscula(contexto.palabra)} de ${zona}` };
    };
    const capacidad = (o: Oferta) => {
        if (o.units_for_sale != null) {
            return o.units_for_sale;
        }
        if (o.target.resource_id !== undefined) {
            return 1;
        }
        return contexto.recursos.filter((r) => r.current_terms.zone_id === o.target.zone_id).length;
    };
    const eventos = [...new Map(datos.ofertas.map((o) => [o.event_id, o.event])).entries()].sort((a, b) => (a[1]?.starts_at ?? '').localeCompare(b[1]?.starts_at ?? ''));
    const eventoDe = (id: string | null | undefined) => datos.ofertas.find((o) => o.event_id === id)?.event;
    const primera = datos.recolocar[0];
    const cambio = primera === undefined ? undefined : eventoDe(primera.event_id);
    const cuando = (inicio: string, fin?: string) => `${diaCorto(hoyEn(contexto.zona, new Date(inicio)))}, ${horaEn(inicio, contexto.zona)}${fin === undefined ? '' : ` a ${horaEn(fin, contexto.zona)}`}`;

    return (
        <>
            <div className="rv-sl-filtro">
                <button type="button" className="rv-sl-filtro__pastilla" aria-label={vista === 'venta' ? `A la venta, ${String(datos.ofertas.length)}` : `Por recolocar, ${String(datos.recolocar.length)}`}>
                    <span>{vista === 'venta' ? 'A la venta' : 'Por recolocar'}</span>
                    <b>{vista === 'venta' ? datos.ofertas.length : datos.recolocar.length}</b>
                </button>
                <button type="button" className="rv-sl-redondo rv-sl-redondo--chico" aria-label="Ver la lista siguiente" onClick={() => { setVista(vista === 'venta' ? 'recolocar' : 'venta'); }}>
                    <Icono nombre="chevron-right" />
                </button>
            </div>
            <p className="rv-rc-explica">
                <Icono nombre="info" />
                <span>
                    <strong>Aquí no se crean reservados: eliges los que ya tienes o zonas enteras y las pones a la venta para un evento.</strong>{' '}
                    <button type="button" className="rv-rc-enlace" onClick={() => { navegar('/recursos?apartado=zonas'); }}>Ver Zonas y recursos</button>
                </span>
            </p>
            {primera !== undefined && vista === 'venta' && (
                <div className="rv-rc-aviso" role="status">
                    <span className="rv-rc-aviso__icono" aria-hidden="true"><Icono nombre="calendar-clock" /></span>
                    <span>
                        <strong>{datos.recolocar.length === 1 ? '1 reserva por recolocar' : `${String(datos.recolocar.length)} reservas por recolocar`}</strong>
                        <small>
                            {cambio === undefined
                                ? 'Un evento ha cambiado y no caben todos. Recolócalos o cancélalos con devolución íntegra.'
                                : `${cambio.public_name ?? 'El evento'} ${cambio.previous_starts_at == null ? 'ha cambiado' : `cambió del ${diaCorto(hoyEn(contexto.zona, new Date(cambio.previous_starts_at)))} al ${diaCorto(hoyEn(contexto.zona, new Date(cambio.starts_at)))}`} y no caben todos. Recolócalos o cancélalos con devolución íntegra.`}
                        </small>
                    </span>
                    <button type="button" className="rv-b rv-b-sec" onClick={() => { setVista('recolocar'); }}><span>Recolocar</span><Icono nombre="arrow-right" /></button>
                </div>
            )}
            {vista === 'venta' ? (
                eventos.length === 0 ? (
                    <p className="rv-sl-nota rv-sl-nota--centro">Aún no has puesto nada a la venta para un evento.</p>
                ) : (
                    eventos.map(([eventoId, ev]) => (
                        <section key={eventoId} className="rv-rc-evento" aria-label={ev?.public_name ?? 'Evento'}>
                            <header>
                                <span className="rv-rc-evento__icono" aria-hidden="true"><Icono nombre="party-popper" /></span>
                                <span><strong>{ev?.public_name ?? 'Evento'}</strong><small>{ev === undefined ? '' : cuando(ev.starts_at, ev.ends_at)}</small></span>
                            </header>
                            {datos.ofertas.filter((o) => o.event_id === eventoId).map((o) => {
                                const d = destino(o);
                                const [texto, chip] = ESTADO[o.status] ?? ['', 'neutral'];
                                return (
                                    <button key={o.id} type="button" className="rv-rc-oferta" aria-label={`${d.nombre}: ${texto}`} onClick={() => { setEditar(o); }}>
                                        <span className="rv-rc-oferta__icono" aria-hidden="true"><Icono nombre={d.icono} /></span>
                                        <span className="rv-rc-oferta__nombre"><strong>{d.nombre}</strong><small>{[d.tipo, PRECIO[o.price_mode ?? ''] ?? '', o.public === false ? 'no se ve en la web' : ''].filter((x) => x !== '').join(' • ')}</small></span>
                                        <span className="rv-rc-oferta__dato"><small>Precio</small><strong>{dinero(o.price)}</strong></span>
                                        <span className="rv-rc-oferta__dato"><small>Vendidos</small><strong>{`${String(o.sold_units ?? 0)} de ${String(capacidad(o))}`}</strong></span>
                                        <span className="rv-rc-oferta__dato"><small>Cupo RRPP</small><strong>{(o.promoter_quota ?? 0) > 0 ? `${String(o.promoter_used ?? 0)} de ${String(o.promoter_quota ?? 0)}` : '—'}</strong></span>
                                        <Chip estado={chip}>{texto}</Chip>
                                        <Icono nombre="chevron-right" />
                                    </button>
                                );
                            })}
                        </section>
                    ))
                )
            ) : datos.recolocar.length === 0 ? (
                <p className="rv-sl-nota rv-sl-nota--centro">No hay reservas por recolocar.</p>
            ) : (
                datos.recolocar.map((r) => (
                    <PorRecolocar key={r.id} ops={ops} negocio={negocio} reserva={r} nombre={nombreDe(r, nombres)} contexto={contexto} gestiona={gestiona} evento={eventoDe(r.event_id)} onHecho={recargar} />
                ))
            )}
            {(editar !== null || nueva) && (
                <PanelOferta
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    contexto={contexto}
                    oferta={editar}
                    gestiona={gestiona && !soloLectura}
                    puedePoliticas={permisos.puede('pagos.politicas.gestionar')}
                    onCerrar={(texto) => {
                        setEditar(null);
                        setNuevaCerrada(nuevo);
                        if (texto !== undefined) {
                            avisar({ tipo: 'ok', texto });
                            recargar();
                        }
                    }}
                />
            )}
        </>
    );
}

function PorRecolocar({ ops, negocio, reserva, nombre, contexto, gestiona, evento, onHecho }: {
    ops: Operaciones;
    negocio: string;
    reserva: Reserva;
    nombre: string;
    contexto: ContextoReservas;
    gestiona: boolean;
    evento: Oferta['event'] | undefined;
    onHecho: () => void;
}) {
    const avisar = useAvisoBreve();
    const [donde, setDonde] = useState('');
    const [confirmar, setConfirmar] = useState(false);
    const [cancelar, setCancelar] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const actual = reserva.resources.map((a) => `${mayuscula(contexto.palabra)} ${contexto.recursos.find((x) => x.id === a.resource_id)?.code ?? ''}`).join(' + ');
    const opciones = useMemo(
        () => [
            { valor: '', texto: 'Elige dónde' },
            ...contexto.recursos
                .filter((x) => x.current_terms.max_capacity >= reserva.party_size)
                .map((x) => ({ valor: x.id, texto: `${mayuscula(contexto.palabra)} ${x.code ?? ''} • ${contexto.zonas.find((z) => z.id === x.current_terms.zone_id)?.name ?? ''} • ${String(x.current_terms.min_capacity)} a ${String(x.current_terms.max_capacity)}` })),
        ],
        [contexto, reserva.party_size],
    );
    const fallo = (e: unknown, defecto: string) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : defecto) }); };
    const recolocar = (igualmente: boolean) => {
        setOcupado(true);
        ops.assignReservationResources(negocio, reserva.id, { resource_ids: [donde], confirm_different_resource: igualmente })
            .then(() => { avisar({ tipo: 'ok', texto: `${nombre} recolocada. Le avisamos del cambio.` }); setConfirmar(false); onHecho(); })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'confirmation_required') {
                    setConfirmar(true);
                } else {
                    fallo(e, 'No se ha podido recolocar.');
                }
            })
            .finally(() => { setOcupado(false); });
    };
    const nuevo = contexto.recursos.find((x) => x.id === donde);
    return (
        <article className="rv-rc-recolocar" aria-label={nombre}>
            <header>
                <span><strong>{nombre}</strong><small>{[actual, `${String(reserva.party_size)} personas`, reserva.total === undefined ? '' : `total ${dinero(reserva.total)}`].filter((x) => x !== '').join(' • ')}</small></span>
                <Chip estado="pending">Por recolocar</Chip>
            </header>
            {evento !== undefined && (
                <p className="rv-sl-nota">
                    <Icono nombre="calendar-clock" />
                    {` ${evento.public_name ?? 'El evento'} ${evento.previous_starts_at == null ? 'ha cambiado' : `pasa del ${diaCorto(hoyEn(contexto.zona, new Date(evento.previous_starts_at)))} al ${diaCorto(hoyEn(contexto.zona, new Date(evento.starts_at)))}`}.`}
                </p>
            )}
            {gestiona && (
                <div className="rv-rc-recolocar__acciones">
                    <Desplegable etiqueta="Recolocar en" valor={donde} opciones={opciones} onChange={setDonde} />
                    <button type="button" className="rv-b rv-b-sec" disabled={donde === '' || ocupado} onClick={() => { recolocar(false); }}><Icono nombre="arrow-right" /><span>Recolocar</span></button>
                    <button type="button" className="rv-b rv-b-sec rv-b-rojo" disabled={ocupado} onClick={() => { setCancelar(true); }}><Icono nombre="circle-x" /><span>Cancelar y devolver</span></button>
                </div>
            )}
            <Ventana
                abierta={confirmar}
                titulo="Recurso diferente: confírmalo o elige otro"
                texto={`${nombre} reservó ${actual || 'otro sitio'}. La recolocas en ${nuevo === undefined ? 'otro' : `${mayuscula(contexto.palabra)} ${nuevo.code ?? ''}`}, con el mismo precio. Queda en Auditoría y le avisamos del cambio.`}
                accion="Recolocar y avisar"
                accionIcono="arrow-right"
                ocupado={ocupado}
                onAccion={() => { recolocar(true); }}
                onCerrar={() => { setConfirmar(false); }}
            />
            <Ventana
                abierta={cancelar}
                titulo="¿Cancelar y devolver?"
                texto={`Cancelamos la reserva de ${nombre}, le devolvemos todo lo que pagó y se lo decimos.`}
                accion="Cancelar y devolver"
                accionIcono="circle-x"
                cancelar="Volver"
                ocupado={ocupado}
                onAccion={() => {
                    setOcupado(true);
                    ops.cancelReservation(negocio, reserva.id, 'El evento cambió de fecha y no hay sitio')
                        .then(() => { avisar({ tipo: 'ok', texto: 'Reserva cancelada. Le devolvemos lo que pagó.' }); setCancelar(false); onHecho(); })
                        .catch((e: unknown) => { fallo(e, 'No se ha podido cancelar.'); })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setCancelar(false); }}
            />
        </article>
    );
}

function PanelOferta({ ops, negocio, movil, contexto, oferta, gestiona, puedePoliticas, onCerrar }: {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    contexto: ContextoReservas;
    oferta: Oferta | null;
    gestiona: boolean;
    puedePoliticas: boolean;
    onCerrar: (texto?: string) => void;
}) {
    const avisar = useAvisoBreve();
    const [eventos, setEventos] = useState<Evento[]>([]);
    const [politicas, setPoliticas] = useState<Politica[]>([]);
    const [f, setF] = useState(() => ({
        evento: oferta?.event_id ?? '',
        tipo: oferta?.target.resource_id !== undefined ? 'recurso' : 'zona',
        destino: oferta?.target.resource_id ?? oferta?.target.zone_id ?? '',
        precio: oferta === null ? '' : textoDeCentimos(oferta.price?.amount ?? null),
        modo: oferta?.price_mode ?? 'per_reservation',
        politica: oferta?.policy_id ?? '',
        unidades: oferta?.units_for_sale == null ? '' : String(oferta.units_for_sale),
        cupo: String(oferta?.promoter_quota ?? 0),
        publico: oferta?.public ?? true,
    }));
    const [ocupado, setOcupado] = useState(false);
    const [retirar, setRetirar] = useState(false);
    const moneda = oferta?.price?.currency ?? 'EUR';
    const rrpp = contexto.bloques.includes('listas_rrpp');

    useEffect(() => {
        let vivo = true;
        if (oferta === null) {
            ops.listEvents(negocio, { status: ['published'], from: new Date().toISOString(), limit: 100 }).then((l) => { if (vivo) { setEventos(l.data); } }).catch(() => undefined);
        }
        if (puedePoliticas) {
            ops.listPaymentPolicies(negocio, { status: 'active', limit: 100 }).then((l) => { if (vivo) { setPoliticas(l.data); } }).catch(() => undefined);
        }
        return () => { vivo = false; };
    }, [ops, negocio, oferta, puedePoliticas]);

    const centimos = aCentimos(f.precio);
    const valido = f.evento !== '' && f.destino !== '' && centimos !== null;
    const campos = () => ({
        price_mode: f.modo,
        price: { amount: centimos ?? 0, currency: moneda },
        policy_id: f.politica === '' ? null : f.politica,
        units_for_sale: f.unidades.trim() === '' ? null : Number(f.unidades),
        ...(rrpp ? { promoter_quota: Math.max(0, Number(f.cupo) || 0) } : {}),
        public: f.publico,
    });
    const fallo = (e: unknown, defecto: string) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : defecto) }); };
    const guardar = (estado?: 'on_sale' | 'paused' | 'draft') => {
        setOcupado(true);
        const hecho = oferta === null
            ? ops.createEventOffer(negocio, { ...campos(), event_id: f.evento, target: f.tipo === 'zona' ? { zone_id: f.destino } : { resource_id: f.destino }, status: estado ?? 'on_sale' })
            : ops.updateEventOffer(negocio, oferta, { ...campos(), ...(estado === undefined ? {} : { status: estado }) });
        hecho
            .then(() => { onCerrar(oferta === null ? 'Puesto a la venta.' : estado === 'paused' ? 'Pausado.' : estado === 'on_sale' ? 'A la venta otra vez.' : 'Guardado.'); })
            .catch((e: unknown) => { fallo(e, 'No se ha podido guardar.'); })
            .finally(() => { setOcupado(false); });
    };
    const opcionesDestino = f.tipo === 'zona'
        ? contexto.zonas.map((z) => ({ valor: z.id, texto: z.name ?? 'Zona' }))
        : contexto.recursos.map((r) => ({ valor: r.id, texto: `${mayuscula(contexto.palabra)} ${r.code ?? ''} • ${contexto.zonas.find((z) => z.id === r.current_terms.zone_id)?.name ?? ''}` }));
    const nombreEvento = oferta?.event?.public_name ?? eventos.find((e) => e.id === f.evento)?.name ?? '';

    return (
        <PanelLateral
            abierto
            movil={movil}
            onCerrar={() => { onCerrar(); }}
            icono="ticket"
            eyebrow="Reservados para eventos"
            titulo={oferta === null ? 'Poner reservados a la venta' : nombreEvento || 'Reservado para un evento'}
            subtitulo={oferta === null ? 'Una zona o un reservado que ya tienes, a la venta para un evento.' : ESTADO[oferta.status]?.[0] ?? ''}
            {...(gestiona ? { accion: oferta === null ? 'Poner a la venta' : 'Guardar', accionIcono: oferta === null ? ('ticket' as const) : ('check' as const), ocupado, inactiva: !valido, onAccion: () => { guardar(); } } : {})}
        >
            <div className="rv-sl-form">
                <h3 className="rv-rs-subtitulo">Evento y destino</h3>
                {oferta === null ? (
                    <Desplegable etiqueta="Evento" valor={f.evento} opciones={[{ valor: '', texto: 'Elige el evento' }, ...eventos.map((e) => ({ valor: e.id, texto: `${e.name ?? 'Evento'} • ${diaCorto(e.local_start_date)}` }))]} onChange={(v) => { setF({ ...f, evento: v }); }} bloqueado={!gestiona} />
                ) : (
                    <dl className="rv-rs-presupuesto"><div><dt>Evento</dt><dd>{nombreEvento || '—'}</dd></div></dl>
                )}
                <Segmentos etiqueta="Qué se vende" movil={movil} items={[{ etiqueta: 'Una zona', icono: 'layers' }, { etiqueta: `Un ${contexto.palabra}`, icono: 'layout-grid' }]} valor={f.tipo === 'zona' ? 0 : 1} onCambio={(i) => { if (oferta === null) { setF({ ...f, tipo: i === 0 ? 'zona' : 'recurso', destino: '' }); } }} />
                <Desplegable etiqueta={f.tipo === 'zona' ? 'Zona' : mayuscula(contexto.palabra)} valor={f.destino} opciones={[{ valor: '', texto: 'Elige' }, ...opcionesDestino]} onChange={(v) => { setF({ ...f, destino: v }); }} bloqueado={!gestiona || oferta !== null} />
                <h3 className="rv-rs-subtitulo">Precio y cobro</h3>
                <CampoImporte etiqueta="Precio" valor={f.precio} moneda={moneda} onChange={(v) => { setF({ ...f, precio: v }); }} bloqueado={!gestiona} ayuda="El comprador paga además los gastos de gestión." />
                <Desplegable etiqueta="Cómo se cobra" valor={f.modo} opciones={MODO_PRECIO.map((m) => ({ valor: m, texto: mayuscula(PRECIO[m] ?? '') }))} onChange={(v) => { setF({ ...f, modo: v as ModoPrecio }); }} bloqueado={!gestiona} />
                <Desplegable etiqueta="Política de cobro" valor={f.politica} opciones={[{ valor: '', texto: 'La de la zona o del reservado' }, ...politicas.map((p) => ({ valor: p.id, texto: p.name }))]} onChange={(v) => { setF({ ...f, politica: v }); }} bloqueado={!gestiona || !puedePoliticas} />
                <Campo etiqueta="Unidades a la venta (vacío = todas)" tipo="number" valor={f.unidades} onChange={(v) => { setF({ ...f, unidades: v }); }} bloqueado={!gestiona} />
                {rrpp && <Campo etiqueta="Cupo para RRPP (personas)" tipo="number" valor={f.cupo} onChange={(v) => { setF({ ...f, cupo: v }); }} bloqueado={!gestiona} />}
                <Interruptor etiqueta="Se ve en la web" valor={f.publico} onChange={(v) => { setF({ ...f, publico: v }); }} bloqueado={!gestiona} />
                {gestiona && oferta !== null && (
                    <div className="rv-rs-acciones">
                        {oferta.status === 'on_sale' && <button type="button" className="rv-b rv-b-sec" disabled={ocupado} onClick={() => { guardar('paused'); }}><Icono nombre="pause" /><span>Pausar la venta</span></button>}
                        {(oferta.status === 'paused' || oferta.status === 'draft') && <button type="button" className="rv-b rv-b-sec" disabled={ocupado || !valido} onClick={() => { guardar('on_sale'); }}><Icono nombre="play" /><span>Poner a la venta</span></button>}
                        <button type="button" className="rv-b rv-b-sec rv-b-rojo" disabled={ocupado} onClick={() => { setRetirar(true); }}><Icono nombre="circle-x" /><span>Retirar</span></button>
                    </div>
                )}
                {gestiona && oferta === null && <button type="button" className="rv-b rv-b-sec" disabled={ocupado || !valido} onClick={() => { guardar('draft'); }}><Icono nombre="file-text" /><span>Guardar como borrador</span></button>}
            </div>
            <Ventana
                abierta={retirar}
                titulo="¿Retirar de la venta?"
                texto="Deja de venderse para este evento. Lo ya vendido se mantiene."
                accion="Retirar"
                accionIcono="circle-x"
                cancelar="Volver"
                ocupado={ocupado}
                onAccion={() => {
                    if (oferta === null) {
                        return;
                    }
                    setOcupado(true);
                    ops.withdrawEventOffer(negocio, oferta.id)
                        .then(() => { setRetirar(false); onCerrar('Retirado de la venta.'); })
                        .catch((e: unknown) => { fallo(e, 'No se ha podido retirar.'); })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setRetirar(false); }}
            />
        </PanelLateral>
    );
}
